// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import "../../../style/sassStyle";
import { cx } from "style/utils";

import {
  DummyEvent,
  QuestionnaireInputProps,
  SelectOption,
} from "./QuestionnaireTypes";
import React, { useCallback } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";

import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import QuestionnaireStandardFormControl from "./QuestionnaireStandardFormControl";

const useButtonStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      whiteSpace: "nowrap",
      borderRadius: "20px",
      margin: "5px",
    },
    gridButton: {
      width: "100%",
    },
    groupLabel: {
      marginTop: "10px",
    },
  })
);

const QuestionnaireInputRadioButton = (props: QuestionnaireInputProps) => {
  const { question, onChange } = props;
  const classes = useButtonStyles();
  if (question.type !== "radiobutton") {
    throw Error(
      `Invalid question type ${question.type} for QuestionnaireInputButtonGroup.`
    );
  }
  if (!question.selectOptions) {
    throw Error(
      `Question object passed to QuestionnaireRadioButton needs selectOptions`
    );
  }

  const onChangeWrapper = useCallback(
    event => {
      let value = event.currentTarget.value;
      if (value === question.value) {
        value = undefined;
      }
      const dummyEvent: DummyEvent = {
        currentTarget: {
          name: question.name,
          value,
        },
      };
      onChange(dummyEvent);
    },
    [onChange, question.name, question.value]
  );

  const fieldId = question.name + "-field";

  const makeButton = (selectOption: SelectOption) => (
    <Button
      color={
        question.value + "" === selectOption.value + ""
          ? "primary"
          : "secondary"
      }
      className={cx({
        [classes.button]: true,
        [classes.gridButton]: question.gridLayout || false,
        ...(question.classNames?.button
          ? { [question.classNames.button]: true }
          : {}),
      })}
      variant="contained"
      name={question.name}
      key={selectOption.value}
      value={selectOption.value}
      onClick={onChangeWrapper}
    >
      {selectOption.name}
    </Button>
  );

  return (
    <QuestionnaireStandardFormControl
      error={question.error}
      label={question.label}
      fieldId={fieldId}
      mandatory={question.mandatory}
    >
      <ButtonGroup aria-label="outlined primary button group" color="primary">
        {question.gridLayout ? (
          <Grid container spacing={2}>
            {question.selectOptions.map(selectOption => (
              <Grid item xs={selectOption.grid?.xs} sm={selectOption.grid?.sm}>
                {makeButton(selectOption)}
              </Grid>
            ))}
          </Grid>
        ) : (
          <div>{question.selectOptions.map(makeButton)}</div>
        )}
      </ButtonGroup>
    </QuestionnaireStandardFormControl>
  );
};

export default QuestionnaireInputRadioButton;
